import posthog from 'posthog-js';

// PostHog project API key — public by design, safe to ship in the frontend bundle.
const POSTHOG_API_KEY = 'phc_sMjcXPP3DAtEyjpp9vMmmLtnrzJ9ZRGZrP3EuTysVXm7';
const POSTHOG_API_HOST = 'https://eu.i.posthog.com';

const LOGIN_TRACKED_KEY = 'bfp_login_tracked';
const MAX_ERROR_EVENTS_PER_LOAD = 20;

// The agreed KPI event set — do not add names here without agreement with the
// analytics owners; they want the data kept to exactly this set.
export type AnalyticsEvent =
  | 'user_logged_in'
  | 'user_logged_out'
  | 'project_opened'
  | 'project_created'
  | 'stope_selected'
  | 'recipe_generated'
  | 'recipe_saved'
  | 'pour_created'
  | 'pour_note_generated'
  | 'approval_requested'
  | 'approval_completed'
  | 'pour_completed'
  | 'report_downloaded'
  | 'data_imported'
  | 'qaqc_imported'
  | 'scenario_created'
  | 'error_occurred';

export interface AnalyticsUser {
  email: string;
  name: string;
  roles: string[];
}

let initialised = false;
let identifiedEmail: string | null = null;
let errorEventCount = 0;

const getAnalyticsEnvironment = (): string => {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.includes('preview')) return 'preview';
  if (hostname.includes('staging')) return 'staging';
  return 'production';
};

export const trackEvent = (event: AnalyticsEvent, properties?: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics must never break the app.
  }
};

// Message only, truncated — no stack traces (they can contain user data).
const trackError = (message: unknown) => {
  if (errorEventCount >= MAX_ERROR_EVENTS_PER_LOAD) return;
  errorEventCount += 1;
  trackEvent('error_occurred', {
    error_message: String(message ?? 'Unknown error').slice(0, 500),
    page: window.location.pathname,
  });
};

export const initAnalytics = () => {
  if (initialised) return;
  initialised = true;

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      autocapture: false,
      disable_session_recording: true,
    });
    posthog.register({ environment: getAnalyticsEnvironment() });

    window.addEventListener('error', (event) => trackError(event.message));
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason as { message?: string } | undefined;
      trackError(reason?.message ?? event.reason);
    });
  } catch {
    // Analytics must never break the app.
  }
};

export const trackPageView = (path: string) => {
  try {
    posthog.capture('$pageview', { path });
  } catch {
    // Analytics must never break the app.
  }
};

export const identifyUser = (user: AnalyticsUser) => {
  if (!user.email || identifiedEmail === user.email) return;
  identifiedEmail = user.email;

  try {
    const role = user.roles.join(', ');
    posthog.identify(user.email, {
      email: user.email,
      name: user.name,
      role,
    });

    // OIDC re-authenticates silently on every page load; only count a login
    // once per browser session so the login KPI is not inflated by refreshes.
    if (!window.sessionStorage.getItem(LOGIN_TRACKED_KEY)) {
      window.sessionStorage.setItem(LOGIN_TRACKED_KEY, '1');
      trackEvent('user_logged_in', { role });
    }
  } catch {
    // Analytics must never break the app.
  }
};

// Enriches the identified person with context that only becomes known after login —
// company (client), site (project) and licence type live on the project, not the user.
export const setUserProperties = (properties: Record<string, unknown>) => {
  try {
    posthog.setPersonProperties(properties);
  } catch {
    // Analytics must never break the app.
  }
};

const PROJECT_SUPER_PROPERTIES = ['project_id', 'project_name', 'client'];

export interface AnalyticsProject {
  id: string;
  /** Unknown until the project has loaded. */
  name?: string;
  client?: string;
}

// Attaches the open project (the "model") to EVERY event sent while it is open, so usage can be
// reported per model without each call site having to pass it. Pass null when the project closes.
export const setAnalyticsProject = (project: AnalyticsProject | null) => {
  try {
    if (project) {
      // Drop the previous project first so its name cannot linger next to a new id.
      PROJECT_SUPER_PROPERTIES.forEach((property) => posthog.unregister(property));
      posthog.register({
        project_id: project.id,
        ...(project.name ? { project_name: project.name } : {}),
        ...(project.client ? { client: project.client } : {}),
      });
    } else {
      PROJECT_SUPER_PROPERTIES.forEach((property) => posthog.unregister(property));
    }
  } catch {
    // Analytics must never break the app.
  }
};

export const resetAnalytics = () => {
  identifiedEmail = null;
  try {
    window.sessionStorage.removeItem(LOGIN_TRACKED_KEY);
    posthog.reset();
  } catch {
    // Analytics must never break the app.
  }
};
